import React, { Suspense, lazy, useEffect } from "react"
import { BrowserRouter as Router, Switch } from "react-router-dom"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import { GuestRoute, PrivateRoute } from "./helpers/routes"
import LoadingSkeleton from "./common/LoadingSkeleton"
import OfflineIndicator from "./common/OfflineIndicator"
import PWAInstallBanner from "./common/PWAInstallBanner"
import PWANavBar from "./common/PWANavBar"
import ScrollToTop from "./common/ScrollToTop"
import UpdateAvailableBanner from "./common/UpdateAvailableBanner"
import ViewAsBanner from "./common/ViewAsBanner"
import "normalize.css"
import "semantic-ui-css/semantic.min.css"
import "../styles/main.scss"
import "react-toastify/dist/ReactToastify.css"

// Lazy load guest pages for code splitting
const Login = lazy(() => import("./pages/Login"))
const Buy = lazy(() => import("./pages/Buy"))
const PasswordEmail = lazy(() => import("./pages/PasswordEmail"))
const PasswordReset = lazy(() => import("./pages/PasswordReset"))
const PersonalAccountContainer = lazy(() => import("./containers/PersonalAccountContainer"))
const HelpInstall = lazy(() => import("./pages/HelpInstall"))

toast.configure({
	position: toast.POSITION.BOTTOM_RIGHT,
	autoClose: 3000,
})

const App = ({ user }) => {
	// Detect standalone mode and add body class
	useEffect(() => {
		const isStandalone =
			window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true

		if (isStandalone) {
			document.body.classList.add("pwa-standalone")
		}

		// Listen for changes
		const mediaQuery = window.matchMedia("(display-mode: standalone)")
		const handleChange = e => {
			if (e.matches) {
				document.body.classList.add("pwa-standalone")
			} else {
				document.body.classList.remove("pwa-standalone")
			}
		}
		mediaQuery.addEventListener("change", handleChange)

		return () => {
			mediaQuery.removeEventListener("change", handleChange)
		}
	}, [])

	return (
		<div className="App">
			{/* Outside the Router and above every screen, admin and student alike: an */}
			{/* admin who has forgotten they are viewing as a child is the whole risk */}
			{/* this guards against. It renders nothing unless one is live. */}
			{user && <ViewAsBanner />}
			<OfflineIndicator />
			<Router>
				<ScrollToTop />
				<Suspense fallback={<LoadingSkeleton />}>
					<Switch>
						<GuestRoute exact loggedIn={user} path="/login" component={Login} />
						<GuestRoute exact loggedIn={user} path="/password/email" component={PasswordEmail} />
						<GuestRoute exact loggedIn={user} path="/password/reset/:token" component={PasswordReset} />

						<GuestRoute exact loggedIn={user} path="/buy/:id" component={Buy} />
						<GuestRoute exact loggedIn={user} path="/buy/:id/trial" component={Buy} />

						{/* Help Install page - accessible to both logged in and guest users */}
						<PrivateRoute exact loggedIn={user} path="/help/install" component={HelpInstall} />

						<PrivateRoute exact loggedIn={user} path="/:type?/:id?" component={PersonalAccountContainer} />
					</Switch>
				</Suspense>
				{/* PWA components - show for logged in users */}
				{/* Signed-in only: a purchase flow is the wrong place for a reload bar, and a guest */}
				{/* arriving at /login has made the real navigation that updates the worker anyway. */}
				{user && <UpdateAvailableBanner />}
				{user && <PWAInstallBanner />}
				{user && <PWANavBar />}
			</Router>
		</div>
	)
}

export default connect(state => ({
	user: state.authReducer.data.user,
}))(App)
